import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { isPast, isValid } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { MERCHANT_ID, SETTLEMENT_ID, TRANSACTION_ID } from './lohi-pay/lohi-pay-routing.constants';
import { AuthService } from './root/auth.service';
import { RouterStateService } from './root/router-state.service';
import { ServerSentEventsService } from './root/server-sent-events.service';
import { SnackBarService } from './shared/snackbar.service';
import {
  LoHiPayCreditLineInfo,
  LoHiPayMerchant,
  LoHiPayOneTimeCode,
  LoHiPayOneTimeCodeMerchant,
  LoHiPayServerSentEvent,
  LoHiPaySettlement,
  LoHiPaySettlementDetails,
  LoHiPayTransaction,
} from './types';
import { isClientError, isHttpErrorResponse } from './utilities/isHttpErrorResponse';
import { SimpleFinn } from './utilities/simpleFinn';

const ONE_TIME_CODE_LS_KEY = 'LOHI_PAY_ONE_TIME_CODE';

interface Loading {
  oneTimeCode: boolean;
  claimCode: boolean;
  transaction: boolean;
  transactions: boolean;
  settlements: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LohiPayService {
  private loading$$ = new BehaviorSubject<Loading>({
    oneTimeCode: false,
    claimCode: false,
    transaction: false,
    transactions: false,
    settlements: false,
  });
  public loading$ = this.loading$$.pipe(shareReplay(1));
  private oneTimeCode$$ = new BehaviorSubject<LoHiPayOneTimeCode | null>(null);
  public oneTimeCode$ = this.oneTimeCode$$.pipe(shareReplay(1));
  private serverEvents$$ = new BehaviorSubject<LoHiPayServerSentEvent | null>(null);

  private selectedTransaction$$ = new BehaviorSubject<LoHiPayTransaction | null>(null);
  public selectedTransaction$ = this.selectedTransaction$$.pipe(shareReplay(1));

  private selectedSettlement$$ = new BehaviorSubject<LoHiPaySettlementDetails | null>(null);
  public selectedSettlement$ = this.selectedSettlement$$.pipe(shareReplay(1));

  private selectedMerchant$$ = new BehaviorSubject<LoHiPayMerchant | null>(null);
  public selectedMerchant$ = this.selectedMerchant$$.pipe(shareReplay(1));

  private transactionsAsConsumer$$ = new BehaviorSubject<LoHiPayTransaction[]>(null);
  private transactionsAsConsumerThrottle$ = new Subject();
  private transactionsAsConsumerShared$ = this.transactionsAsConsumer$$.pipe(shareReplay(1));
  public get transactionsAsConsumer$() {
    this.transactionsAsConsumerThrottle$.next();
    return this.transactionsAsConsumerShared$;
  }

  private transactionsAsMerchant$$ = new BehaviorSubject<LoHiPayTransaction[]>(null);
  private transactionsAsMerchantThrottle$ = new Subject();
  private transactionsAsMerchantShared$ = this.transactionsAsMerchant$$.pipe(shareReplay(1));
  public get transactionsAsMerchant$() {
    this.transactionsAsMerchantThrottle$.next();
    return this.transactionsAsMerchantShared$;
  }

  private creditLineInfo = new SimpleFinn<LoHiPayCreditLineInfo | null>(null, this.getCreditLineInfo.bind(this));
  public get creditLineInfo$() {
    return this.creditLineInfo.get$();
  }

  private settlementsList = new SimpleFinn<LoHiPaySettlement[] | null>(null, this.getSettlements$.bind(this));
  public get settlementsList$() {
    return this.settlementsList.get$();
  }

  constructor(
    private httpClient: HttpClient,
    private logger: NGXLogger,
    private snackbar: SnackBarService,
    private serverSentEvents: ServerSentEventsService,
    private routerState: RouterStateService,
    private authService: AuthService,
  ) {
    this.tryToLoadOneTimeCodeFromLocalStorage();
    this.listenForServerEvents();
    this.listenForTransaction();
    this.listenForSettlement();
    this.listenForMerchant();
    this.transactionsAsConsumerThrottle$.pipe(debounceTime(1000)).subscribe(() => {
      this.getTransactionsAsConsumer().then((txes) => this.transactionsAsConsumer$$.next(txes));
    });
    this.transactionsAsMerchantThrottle$.pipe(debounceTime(1000)).subscribe(() => {
      this.getTransactionsAsMerchant().then((txes) => this.transactionsAsMerchant$$.next(txes));
    });
  }

  public createOneTimeCode() {
    this.loadingOn('oneTimeCode');
    this.httpClient
      .post<{ oneTimeCode: LoHiPayOneTimeCode }>(`${environment.api}/v2/lohi_pay/consumer/one_time_code`, {})
      .subscribe({
        error: (err) => {
          this.logger.error(err);
          if (isHttpErrorResponse(err)) {
            if (isClientError(err)) {
              this.snackbar.showError(err.messageToUser);
            } else {
              this.snackbar.showError('Failed to generate code, please contact support');
            }
          }
          this.loadingOff('oneTimeCode');
        },
        next: (resp) => {
          this.setOneTimeCode(resp.oneTimeCode);
          this.loadingOff('oneTimeCode');
        },
      });
  }

  public merchantClaimCode$(code: string): Observable<LoHiPayOneTimeCodeMerchant> {
    this.loadingOn('claimCode');
    return this.httpClient
      .post<{ oneTimeCode: LoHiPayOneTimeCodeMerchant }>(`${environment.api}/v2/lohi_pay/merchant/claim_code`, {
        code,
      })
      .pipe(map((resp) => resp.oneTimeCode));
  }

  public merchantCreateTransactionFromCode$(
    codeId: number,
    description: string,
    totalCents: number,
    internalId: string | null,
  ): Observable<LoHiPayTransaction> {
    return this.httpClient
      .post<{ transaction: LoHiPayTransaction }>(
        `${environment.api}/v2/lohi_pay/merchant/create_transaction_from_code`,
        {
          codeId,
          description,
          totalCents,
          internalId,
        },
      )
      .pipe(map((resp) => resp.transaction));
  }

  public consumerApproveSelectedTransaction$(): Observable<LoHiPayTransaction> {
    return this.httpClient
      .patch<{ transaction: LoHiPayTransaction }>(
        `${environment.api}/v2/lohi_pay/consumer/transaction/${this.selectedTransaction$$.value?.id}/approve`,
        {},
      )
      .pipe(
        tap({
          next: (resp) => {
            if (this.selectedTransaction$$.value?.id === resp?.transaction.id) {
              this.selectedTransaction$$.next(resp.transaction);
            }
          },
        }),
        map((resp) => resp.transaction),
      );
  }

  public consumerRejectSelectedTransaction$(): Observable<LoHiPayTransaction> {
    return this.httpClient
      .patch<{ transaction: LoHiPayTransaction }>(
        `${environment.api}/v2/lohi_pay/consumer/transaction/${this.selectedTransaction$$.value?.id}/reject`,
        {},
      )
      .pipe(
        tap({
          next: (resp) => {
            if (this.selectedTransaction$$.value?.id === resp?.transaction.id) {
              this.selectedTransaction$$.next(resp.transaction);
            }
          },
        }),
        map((resp) => resp.transaction),
      );
  }

  public updateDriverSpendingLimit$(userId: string, spendingLimitCents: number): Observable<number> {
    return this.httpClient
      .post<{ spendingLimits: Record<string, number> }>(`${environment.api}/v2/lohi_pay/consumer/set_spending_limits`, {
        spendingLimits: [
          {
            userId,
            spendingLimitCents,
          },
        ],
      })
      .pipe(
        map((resp) => {
          return resp.spendingLimits[userId];
        }),
      );
  }

  public removeDriverSpendingLimit$(userId: string): Observable<number> {
    return this.httpClient
      .post<{ spendingLimits: Record<string, number> }>(
        `${environment.api}/v2/lohi_pay/consumer/remove_spending_limit`,
        {
          userId,
        },
      )
      .pipe(
        map((resp) => {
          return resp.spendingLimits[userId];
        }),
      );
  }

  public createTransactionAsConsumer$(
    merchantId: string,
    description: string,
    totalCents: number,
    autoApprove: boolean,
  ): Observable<LoHiPayTransaction> {
    return this.httpClient
      .post<{ transaction: LoHiPayTransaction }>(`${environment.api}/v2/lohi_pay/consumer/transaction`, {
        merchantId,
        description,
        totalCents,
        autoApprove,
      })
      .pipe(map((resp) => resp.transaction));
  }

  private getCreditLineInfo(): Promise<LoHiPayCreditLineInfo> {
    return this.httpClient
      .get<{ creditLineInfo: LoHiPayCreditLineInfo }>(`${environment.api}/v2/lohi_pay/consumer/available_credit`)
      .pipe(map((resp) => resp?.creditLineInfo))
      .toPromise();
  }

  private getCode(id: number) {
    this.loadingOn('oneTimeCode');
    this.httpClient
      .get<{ oneTimeCode: LoHiPayOneTimeCode }>(`${environment.api}/v2/lohi_pay/consumer/one_time_code/${id}`)
      .subscribe({
        error: (err) => {
          this.logger.error(err);
          this.loadingOff('oneTimeCode');
        },
        next: (resp) => {
          if (!resp?.oneTimeCode.transaction) {
            this.setOneTimeCode(resp.oneTimeCode);
          } else {
            localStorage.removeItem(ONE_TIME_CODE_LS_KEY);
          }
          this.loadingOff('oneTimeCode');
        },
      });
  }

  private getTransactionsAsConsumer(): Promise<LoHiPayTransaction[]> {
    this.loadingOn('transactions');
    return this.httpClient
      .get<{ transactions: LoHiPayTransaction[] }>(`${environment.api}/v2/lohi_pay/consumer/transaction`)
      .pipe(
        tap({
          next: () => {
            this.loadingOff('transactions');
          },
        }),
        catchError((err) => {
          this.logger.error(err);
          this.loadingOff('transactions');
          return of(null);
        }),
        map((resp) => resp?.transactions ?? []),
      )
      .toPromise();
  }

  private getSettlements$(): Observable<LoHiPaySettlement[]> {
    this.loadingOn('settlements');
    return this.httpClient
      .get<{ settlements: LoHiPaySettlement[] }>(`${environment.api}/v2/lohi_pay/merchant/settlement`)
      .pipe(
        tap({
          next: () => {
            this.loadingOff('settlements');
          },
        }),
        catchError((err) => {
          this.logger.error(err);
          this.loadingOff('settlements');
          return of(null);
        }),
        map((resp) => resp?.settlements || []),
        map((settlements: LoHiPaySettlement[]) =>
          settlements.map((s) => ({
            ...s,
            dateOfCreation: s.dateOfCreation.replace('Z', ''),
          })),
        ),
      );
  }

  private getTransactionsAsMerchant(): Promise<LoHiPayTransaction[]> {
    this.loadingOn('transactions');
    return this.httpClient
      .get<{ transactions: LoHiPayTransaction[] }>(`${environment.api}/v2/lohi_pay/merchant/transaction`)
      .pipe(
        tap({
          next: () => {
            this.loadingOff('transactions');
          },
        }),
        catchError((err) => {
          this.logger.error(err);
          this.loadingOff('transactions');
          return of(null);
        }),
        map((resp) => resp?.transactions ?? []),
      )
      .toPromise();
  }

  private setOneTimeCode(code: LoHiPayOneTimeCode) {
    localStorage.setItem(ONE_TIME_CODE_LS_KEY, JSON.stringify(code));
    this.oneTimeCode$$.next(code);
  }

  private tryToLoadOneTimeCodeFromLocalStorage() {
    const codeStr = localStorage.getItem(ONE_TIME_CODE_LS_KEY);
    if (!codeStr) {
      return;
    }
    const code: LoHiPayOneTimeCode = JSON.parse(codeStr);
    const date = new Date(code.expiresAt);
    if (!isValid(date) || isPast(date)) {
      return;
    }
    this.getCode(code.id);
  }

  private loadingOn(key: keyof Loading) {
    this.loading$$.next({
      ...this.loading$$.value,
      [key]: true,
    });
  }

  private loadingOff(key: keyof Loading) {
    this.loading$$.next({
      ...this.loading$$.value,
      [key]: false,
    });
  }

  private listenForServerEvents() {
    this.serverSentEvents.messagesFor$('lohiPay').subscribe({
      next: (ev: LoHiPayServerSentEvent) => this.serverEvents$$.next(ev),
    });
    this.listenForOneTimeCodeEvents();
    this.listenForTransactionEvents();
  }

  private loadSingleTransaction$(id: string): Observable<LoHiPayTransaction> {
    return this.httpClient
      .get<{ transaction: LoHiPayTransaction }>(`${environment.api}/v2/lohi_pay/transaction/${id}`)
      .pipe(map((resp) => resp.transaction));
  }

  private loadSingleSettlement$(id: string): Observable<LoHiPaySettlementDetails> {
    return this.httpClient
      .get<{ settlement: LoHiPaySettlementDetails }>(`${environment.api}/v2/lohi_pay/merchant/settlement/${id}`)
      .pipe(
        map((resp) => {
          if (resp?.settlement) {
            return {
              ...resp.settlement,
              dateOfCreation: resp.settlement.dateOfCreation.replace('Z', ''),
            };
          }
          return null;
        }),
      );
  }

  private loadMerchantDetails$(id: string): Observable<LoHiPayMerchant> {
    return this.httpClient
      .get<{ merchant: LoHiPayMerchant }>(`${environment.api}/v2/lohi_pay/consumer/merchant/${id}`)
      .pipe(map((resp) => resp.merchant));
  }

  private listenForOneTimeCodeEvents() {
    this.serverEvents$$.subscribe((se) => {
      if (!se || se.dataType !== 'oneTimeCode') {
        return;
      }
      const data = se.data as LoHiPayOneTimeCode;
      const currentOneTimeCode = this.oneTimeCode$$.value;
      if (currentOneTimeCode && currentOneTimeCode.id === data.id) {
        this.oneTimeCode$$.next(data);
      }
    });
  }

  private listenForTransactionEvents() {
    this.serverEvents$$.subscribe((se) => {
      if (!se || se.dataType !== 'transaction') {
        return;
      }
      const data = se.data as LoHiPayTransaction;
      const currentTransaction = this.selectedTransaction$$.value;
      if (currentTransaction && currentTransaction.id === data.id) {
        this.selectedTransaction$$.next(data);
      }
      this.updateTransactionHistory(data);
    });
  }

  private async updateTransactionHistory(tx: LoHiPayTransaction) {
    const user = await this.authService.userInfo$.pipe(take(1)).toPromise();
    if (user.companyId === tx.merchantCompany.id) {
      const currentTransactions = this.transactionsAsMerchant$$.value || [];
      const currentIndex = currentTransactions.findIndex((t) => t.id === tx.id);
      if (currentIndex !== -1) {
        const cp = [...currentTransactions];
        cp[currentIndex] = tx;
        this.transactionsAsMerchant$$.next(cp);
      } else {
        const withNewValue = [tx, ...currentTransactions].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        this.transactionsAsMerchant$$.next(withNewValue);
      }
    }
    if (user.companyId === tx.company.id) {
      const currentTransactions = this.transactionsAsConsumer$$.value || [];
      const currentIndex = currentTransactions.findIndex((t) => t.id === tx.id);
      if (currentIndex !== -1) {
        const cp = [...currentTransactions];
        cp[currentIndex] = tx;
        this.transactionsAsConsumer$$.next(cp);
      } else {
        const withNewValue = [tx, ...currentTransactions].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        this.transactionsAsConsumer$$.next(withNewValue);
      }
    }
  }

  private listenForTransaction() {
    combineLatest([
      this.routerState.listenForParamChange$(TRANSACTION_ID),
      this.routerState.listenForQueryChange$(TRANSACTION_ID),
    ])
      .pipe(
        map(([param, query]) => {
          if (param) {
            return param;
          }
          if (query) {
            return query;
          }
          return null;
        }),
        distinctUntilChanged(),
        switchMap((transactionId) => {
          if (transactionId) {
            return this.loadSingleTransaction$(transactionId);
          }
          return of(null) as Observable<LoHiPayTransaction>;
        }),
      )
      .subscribe({
        next: (transaction) => {
          this.selectedTransaction$$.next(transaction);
        },
      });
  }

  private listenForSettlement() {
    combineLatest([
      this.routerState.listenForParamChange$(SETTLEMENT_ID).pipe(startWith(null as string)),
      this.routerState.listenForQueryChange$(SETTLEMENT_ID).pipe(startWith(null as string)),
    ])
      .pipe(
        map(([param, query]) => {
          if (param) {
            return param;
          }
          if (query) {
            return query;
          }
          return null;
        }),
        distinctUntilChanged(),
        switchMap((settlementId) => {
          if (settlementId) {
            return this.loadSingleSettlement$(settlementId);
          }
          return of(null) as Observable<LoHiPaySettlementDetails>;
        }),
      )
      .subscribe({
        next: (settlement) => {
          this.selectedSettlement$$.next(settlement);
        },
      });
  }

  private listenForMerchant() {
    combineLatest([
      this.routerState.listenForParamChange$(MERCHANT_ID).pipe(startWith(null as string)),
      this.routerState.listenForQueryChange$(MERCHANT_ID).pipe(startWith(null as string)),
    ])
      .pipe(
        map(([param, query]) => {
          if (param) {
            return param;
          }
          if (query) {
            return query;
          }
          return null;
        }),
        distinctUntilChanged(),
        switchMap((merchantId) => {
          if (merchantId) {
            if (merchantId.length === 8) {
              merchantId = `LCO${merchantId}`;
            }
            return this.loadMerchantDetails$(merchantId);
          }
          return of(null as LoHiPayMerchant);
        }),
      )
      .subscribe({
        next: (merchant) => {
          this.selectedMerchant$$.next(merchant);
        },
      });
  }
}
